import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { RecommendationTemplateRead } from 'store/generated/portal/assessments';
import { Icon } from 'semantic-ui-react';
import { PageSection } from 'common/styles/page';
import OrganizationGoalDetailsModal from './OrganizationGoalDetailsModal';
import { AssessmentPeriodMetricResults } from 'areas/assessment-schedule/api/assessmentPeriodMetricResultsApi';

type GoalColumnCard = {
  id: number;
  title: string;
  details: string;
  priority: string;
  template: any;
};

interface GoalSelectorProps {
  selectedGoalIds: number[];
  setSelectedGoalIds: (goalIds: number[]) => void;
  pillar_data: AssessmentPeriodMetricResults | undefined;
  rec_template_data: any;
  setHasStrengthGoal: (hasStrengthGoal: boolean) => void;
}
const GoalSelector: React.FC<GoalSelectorProps> = ({
  selectedGoalIds,
  setSelectedGoalIds,
  pillar_data,
  rec_template_data,
  setHasStrengthGoal,
}) => {
  const [columns, setColumns] = useState<{
    [key: string]: GoalColumnCard[];
  }>({});
  const [expandedCard, setExpandedCard] = useState<{ [key: string]: number | null }>({});

  const [selectedItem, setSelectedItem] = useState<any | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [strengthPillar, setStrengthPillar] = useState<number | null>(null);

  const handleOpenModal = (item: any) => {
    setSelectedItem(item);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setSelectedItem(null);
    setIsModalOpen(false);
  };

  enum Target {
    DEFICIT = 1,
    STRENGTH = 2,
  }

  const getPillarLabelByFactor = (
    pillarsByFactor: Record<number, string>,
    factor: number
  ): string => pillarsByFactor[factor] || 'Uncategorized';

  // Populate columns with initial structure based on `pillar_data`
  useEffect(() => {
    if (pillar_data?.pillars?.length) {
      const pillars = pillar_data.pillars;
      const sortedPillars = [...pillars].sort(
        (a, b) => (a.average_response ?? 0) - (b.average_response ?? 0)
      );
      const lowestPillars = sortedPillars.slice(0, 2); // Two lowest
      const highestPillar = sortedPillars.slice(-1)[0]; // One highest
      const highestPillarFactor = highestPillar.factor ?? null;

      let selectedPillars = [...lowestPillars];

      if (highestPillar.average_response > 7.5) {
        // 7.5 is minimum average response to receive a strength pillar
        setStrengthPillar(highestPillarFactor);
        setHasStrengthGoal(true);
        selectedPillars = [...lowestPillars, highestPillar];
      } else {
        setStrengthPillar(-1);
        selectedGoalIds[2] = 0; // no strength pillar goal
        setHasStrengthGoal(false);
      }

      const initialColumns = selectedPillars.reduce<Record<string, GoalColumnCard[]>>(
        (acc, pillar) => {
          const pillarName = pillar.label || 'Uncategorized';
          acc[pillarName] = [];
          return acc;
        },
        {}
      );
      setColumns(initialColumns);

      // Initialize expandedCard state
      const initialExpandedCard = Object.keys(initialColumns).reduce(
        (acc, pillar) => {
          acc[pillar] = null;
          return acc;
        },
        {} as { [key: string]: number | null }
      );
      setExpandedCard(initialExpandedCard);
    }
  }, [pillar_data]);

  useEffect(() => {
    if (
      !pillar_data ||
      !pillar_data.pillars?.length ||
      !rec_template_data ||
      !Array.isArray(rec_template_data) ||
      rec_template_data.length === 0 ||
      strengthPillar === null
    ) {
      return;
    }

    const pillarsByFactor = pillar_data.pillars.reduce<Record<number, string>>((acc, pillar) => {
      if (pillar.factor !== undefined && pillar.factor !== null) {
        acc[pillar.factor] = pillar.label;
      }
      return acc;
    }, {});

    const templates = rec_template_data.flatMap((item: RecommendationTemplateRead) =>
      (item.template?.associations || []).map((association: any) => ({
        id: item.id,
        name: item.name || 'Unnamed Template',
        sort_order: item.sort_order ?? null,
        description: association.summaryText || 'No association description available',
        pillar: getPillarLabelByFactor(pillarsByFactor, association.factor),
        factor_id: association.factor,
        priority: association.priority || 'primary',
        template: item.template ?? null,
        target: item.target ?? null,
      }))
    );

    setColumns(prevColumns => {
      const updatedColumns = Object.keys(prevColumns).reduce<Record<string, GoalColumnCard[]>>(
        (acc, key) => {
          acc[key] = [];
          return acc;
        },
        {}
      );

      templates.forEach(
        (template: {
          id: number;
          factor_id: number;
          target: any;
          pillar: string;
          name: any;
          description: any;
          priority: any;
          template: any;
        }) => {
          const factor_id = template.factor_id;
          const target = Number(template.target);
          const pillar = template.pillar || '';

          if (!pillar || !(pillar in updatedColumns)) return;
          if (strengthPillar === -1 && target === Target.STRENGTH) return; // Skip if no strength pillar is defined

          if (target === Target.DEFICIT && factor_id !== strengthPillar) {
            updatedColumns[pillar].push({
              id: template.id,
              title: template.name,
              details: template.description,
              priority: template.priority,
              template: template.template,
            });
            updatedColumns[pillar].sort((a, b) => (a.priority === 'primary' ? -1 : 1));
          } else if (target === Target.STRENGTH && factor_id === strengthPillar) {
            updatedColumns[pillar].push({
              id: template.id,
              title: template.name,
              details: template.description,
              priority: template.priority,
              template: template.template,
            });
          }
        }
      );

      return updatedColumns;
    });
  }, [rec_template_data, pillar_data, strengthPillar]);

  const handleCardToggle = (column: string, index: number, col_index: number, id: number) => {
    setColumns(prev => {
      const updatedColumn = [...prev[column]];
      const [selectedItem] = updatedColumn.splice(index, 1);
      updatedColumn.unshift(selectedItem);
      return {
        ...prev,
        [column]: updatedColumn,
      };
    });

    setExpandedCard(prev => ({
      ...prev,
      [column]: 0, // Set to the first item (top of the column)
    }));

    const updatedGoalIds = [...selectedGoalIds];
    updatedGoalIds[col_index] = id;
    setSelectedGoalIds(updatedGoalIds);
  };

  return (
    <Container>
      {Object.keys(columns).map((col, col_index) => (
        <Column key={col} count={strengthPillar !== -1 ? 3 : 2} data-testid={`goal-column-${col_index}`}>
          <Header>{col}</Header>
          <Subheader>{col_index < 2 ? 'Deficit Goals' : 'Strength Goals'}</Subheader>
          {columns[col].map((item, index) => (
            <Card
              key={item.id}
              onClick={() => handleCardToggle(col, index, col_index, item.id)}
              isExpanded={expandedCard[col] === index}
              priority={item.priority}
            >
              <PageSection display="flex" flexDirection="row" justifyContent="space-between">
                <CardText>{item.title}</CardText>

                {item.priority === 'primary' && <Icon name="star outline" color="grey" />}
              </PageSection>
              <ExpandedText isExpanded={expandedCard[col] === index}>
                {item.details} &nbsp;
                <IconWrapper
                  onClick={e => {
                    e.stopPropagation(); // Prevent triggering `onClick` of `Card`
                    handleOpenModal(item);
                  }}
                >
                  [<Icon name="external alternate" color="grey" size="small" />
                  more]
                </IconWrapper>
              </ExpandedText>
            </Card>
          ))}
        </Column>
      ))}

      <OrganizationGoalDetailsModal
        title={selectedItem?.title}
        details={selectedItem?.details}
        context={selectedItem?.template.context}
        handleCloseModal={handleCloseModal}
        isModalOpen={isModalOpen}
      />
    </Container>
  );
};
export default GoalSelector;

const Container = styled.div`
  display: flex;
  gap: 20px;
  padding-top: 20px;
  justify-content: center;
`;

const Column = styled.div<{ count: number }>`
  display: flex;
  flex-direction: column;
  width: ${props => (props.count === 2 ? '50%' : '33%')};
  background-color: #f9f9f9;
  border: 1px solid #ddd;
  border-radius: 8px;
  padding: 15px;
`;

const Header = styled.div`
  text-align: center;
  font-family: 'Poppins', sans-serif;
  font-size: 18px;
  font-weight: 500;
  font-style: normal;
  color: #414141;
  margin-bottom: 2px;
`;

const Subheader = styled.div`
  text-align: center;
  font-family: 'Poppins', sans-serif;
  font-size: 11px;
  font-weight: 400;
  font-style: normal;
  color: #414141;
  margin-bottom: 15px;
`;

const Card = styled.div<{ isExpanded: boolean; priority: string }>`
  background-color: ${props => (props.isExpanded ? '#d1e7dd' : '#ffffff')};
  border: 1px solid ${props => (props.isExpanded ? '#0f5132' : '#ddd')};
  border-radius: 4px;
  padding: 10px;
  margin-bottom: 10px;
  font-family: 'Poppins', sans-serif;
  font-size: 14px;
  cursor: pointer;
  transition:
    background-color 0.2s,
    border-color 0.2s,
    all 0.2s ease-in-out;

  &:hover > div {
    max-height: 500px;
    opacity: 1;
    visibility: visible;
  }

  &:hover {
    background-color: ${props => (props.isExpanded ? '#d1e7dd' : '#f1f1f1')};
  }
`;

const CardText = styled.div`
  margin-bottom: 5px;
`;

const ExpandedText = styled.div<{ isExpanded: boolean }>`
  margin-top: 10px;
  font-size: 12px;
  color: #555;
  line-height: 1.5;
  max-height: ${props => (props.isExpanded ? '500px' : '0')};
  opacity: ${props => (props.isExpanded ? '1' : '0')};
  visibility: ${props => (props.isExpanded ? 'visible' : 'hidden')};
  overflow: hidden;
  transition:
    max-height 0.6s ease-in-out,
    opacity 0.6s ease-in-out,
    visibility 0.6s ease-in-out;
`;

const IconWrapper = styled.span`
  cursor: pointer;
  display: inline-flex;
  align-items: center;
`;
